import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { content } from "../../data/content";
import { burstConfetti } from "../../lib/confetti";
import { Reveal } from "./Reveal";

function Ball({ side }) {
  return (
    <motion.span
      className="block"
      animate={{ x: side * 90, y: [0, -70, 0], rotate: side * 200 }}
      transition={{ duration: 0.55, ease: "easeInOut" }}
    >
      <svg width="86" height="86" viewBox="0 0 100 100" className="drop-shadow-[3px_5px_0_rgba(28,25,23,0.3)]">
        <circle cx="50" cy="50" r="46" fill="#FFFFFF" stroke="#1C1917" strokeWidth="4" />
        <path d="M50 6 C40 30 40 70 50 94" fill="none" stroke="#1C1917" strokeWidth="3" />
        <path d="M12 28 C40 44 64 44 90 26" fill="none" stroke="#1C1917" strokeWidth="3" />
        <path d="M10 70 C38 56 66 58 92 74" fill="none" stroke="#1C1917" strokeWidth="3" />
      </svg>
    </motion.span>
  );
}

export function Volleyball() {
  const [hits, setHits] = useState(0);
  const rallies = content.volleyball.rallies;
  const done = hits >= rallies.length;

  const bump = () => {
    if (done) return;
    const next = hits + 1;
    setHits(next);
    if (next >= rallies.length) setTimeout(() => burstConfetti(), 300);
  };

  const side = hits % 2 === 0 ? -1 : 1;
  const line = hits === 0 ? content.volleyball.subtitle : rallies[Math.min(hits - 1, rallies.length - 1)];

  return (
    <section
      data-testid="volleyball-section"
      className="relative w-full bg-sky text-white px-5 md:px-10 py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <p className="font-accent text-3xl md:text-5xl text-sun -rotate-2 mb-1">
            {content.volleyball.kicker}
          </p>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl">
            {content.volleyball.title}
          </h2>
        </Reveal>

        {/* rally line */}
        <div className="h-16 mt-8 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={line}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="font-body font-bold text-lg md:text-2xl"
            >
              {line}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* court */}
        <div className="relative mt-6 h-56 md:h-64 flex items-center justify-center">
          {/* net */}
          <div className="absolute left-1/2 top-6 bottom-10 -translate-x-1/2 w-[3px] bg-white/80" />
          <div
            className="absolute left-1/2 top-6 -translate-x-1/2 w-40 md:w-56 h-8 border-[3px] border-white/80 border-b-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, rgba(255,255,255,0.6) 0 2px, transparent 2px 10px), repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 2px, transparent 2px 10px)",
            }}
          />
          {/* ball button */}
          <motion.button
            data-testid="volley-ball"
            onClick={bump}
            whileTap={{ scale: 0.85 }}
            className="relative z-10 outline-none"
            aria-label="Golpear el balón"
          >
            <AnimatePresence mode="popLayout">
              {!done ? (
                <Ball key={hits} side={side} />
              ) : (
                <motion.span
                  key="final"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="block"
                >
                  <svg width="100" height="100" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="46" fill="#FFFFFF" stroke="#1C1917" strokeWidth="4" />
                    <path d="M50 6 C40 30 40 70 50 94" fill="none" stroke="#1C1917" strokeWidth="3" />
                    <path d="M12 28 C40 44 64 44 90 26" fill="none" stroke="#1C1917" strokeWidth="3" />
                    <path d="M10 70 C38 56 66 58 92 74" fill="none" stroke="#1C1917" strokeWidth="3" />
                  </svg>
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          {/* ground line */}
          <div className="absolute left-0 right-0 bottom-8 h-[3px] bg-white/40" />
        </div>

        <div className="h-14 mt-2 flex items-center justify-center">
          <AnimatePresence>
            {done && (
              <motion.p
                data-testid="volley-done"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="font-accent text-4xl md:text-6xl text-sun"
              >
                {content.volleyball.done}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
